<template>
  <div class="message-management">
    <div class="operation-bar">
      <h2>留言管理</h2>
    </div>

    <!-- 移动端卡片布局 -->
    <div class="mobile-cards" v-if="isMobile">
      <el-card v-for="message in messages" :key="message.id" class="message-card">
        <div class="message-info">
          <div class="user-info">
            <h3>{{ message.name }}</h3>
            <div class="contact-info">
              <span>{{ message.phone }}</span>
              <span>{{ message.email }}</span>
            </div>
          </div>
          <div class="message-content">{{ message.message }}</div>
          <div class="message-time">{{ formatTime(message.createTime) }}</div>
        </div>
        <div class="card-actions">
          <el-button type="danger" size="small" @click="deleteMessage(message.id)">删除</el-button>
        </div>
      </el-card>
      
      <!-- 分页器 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <!-- 桌面端表格布局 -->
    <el-table v-else :data="messages" v-loading="loading">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="name" label="姓名" width="120" />
      <el-table-column prop="phone" label="电话" width="150" />
      <el-table-column prop="email" label="邮箱" width="200" />
      <el-table-column prop="message" label="留言内容" show-overflow-tooltip />
      <el-table-column prop="createTime" label="提交时间" width="180">
        <template #default="scope">
          {{ formatTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="120" fixed="right">
        <template #default="scope">
          <el-button 
            type="danger" 
            size="small" 
            @click="deleteMessage(scope.row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 桌面端分页器 -->
    <div v-if="!isMobile" class="pagination">
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :total="total"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import request from '@/utils/request'

export default {
  name: 'MessageManagement',
  setup() {
    const messages = ref([])
    const loading = ref(false)
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    const isMobile = ref(false)

    const fetchMessages = async () => {
      loading.value = true
      try {
        const params = {
          page: currentPage.value - 1,
          size: pageSize.value,
          sort: 'createTime,desc'
        }
        const response = await request.get('/messages', { params })
        messages.value = response.content
        total.value = response.totalElements
      } catch (error) {
        console.error('获取留言列表失败:', error)
        ElMessage.error('获取留言列表失败')
      } finally {
        loading.value = false
      }
    }

    const deleteMessage = async (id) => {
      try {
        await ElMessageBox.confirm('确定要删除该留言吗？')
        await request.delete(`/messages/${id}`)
        ElMessage.success('删除成功')
        fetchMessages()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }

    const formatTime = (time) => {
      if (!time) return ''
      return new Date(time).toLocaleString()
    }

    const handleSizeChange = (val) => {
      pageSize.value = val
      fetchMessages()
    }

    const handleCurrentChange = (val) => {
      currentPage.value = val
      fetchMessages()
    }

    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768
    }

    onMounted(() => {
      fetchMessages()
      checkMobile()
      window.addEventListener('resize', checkMobile)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', checkMobile)
    })

    return {
      messages,
      loading,
      currentPage,
      pageSize,
      total,
      isMobile,
      deleteMessage,
      formatTime,
      handleSizeChange,
      handleCurrentChange
    }
  }
}
</script>

<style scoped>
.message-management {
  padding: 20px;
}

.operation-bar {
  margin-bottom: 20px;
}

.operation-bar h2 {
  margin: 0;
  font-size: 24px;
  color: #303133;
}

.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.message-card {
  margin: 0;
}

.message-info {
  margin-bottom: 15px;
}

.user-info {
  margin-bottom: 10px;
}

.user-info h3 {
  margin: 0 0 5px;
  font-size: 16px;
  color: #303133;
}

.contact-info {
  display: flex;
  gap: 10px;
  font-size: 14px;
  color: #606266;
}

.message-content {
  font-size: 14px;
  line-height: 1.5;
  color: #303133;
  margin-bottom: 10px;
  white-space: pre-wrap;
}

.message-time {
  font-size: 12px;
  color: #909399;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

@media (max-width: 768px) {
  .message-management {
    padding: 0;
  }

  .operation-bar {
    position: sticky;
    top: 0;
    background: #fff;
    padding: 15px;
    margin: 0;
    z-index: 10;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .operation-bar h2 {
    font-size: 20px;
  }

  .mobile-cards {
    padding: 15px;
  }

  .pagination {
    background: #fff;
    padding: 10px 0;
    margin: 0;
    position: sticky;
    bottom: 0;
    box-shadow: 0 -2px 4px rgba(0,0,0,0.1);
  }
}
</style> 